import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import SEO from '../../components/SEO';
import './IntakeForm.css';
// Generate a unique session ID
const generateSessionId = () => {
    return `intake_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
};
const IntakeForm = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [sessionId] = useState(() => {
        // Get or create session ID from sessionStorage
        const stored = sessionStorage.getItem('intake_session_id');
        if (stored)
            return stored;
        const newId = generateSessionId();
        sessionStorage.setItem('intake_session_id', newId);
        return newId;
    });
    const [currentStep, setCurrentStep] = useState(1);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        company: '',
        projectDetails: '',
        budget: '',
        recaptchaToken: null,
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState(null);
    const [submitSuccess, setSubmitSuccess] = useState(false);
    // Debounce timer for auto-save
    const saveTimeoutRef = useRef(null);
    // Get challenge from URL query params
    useEffect(() => {
        const challenge = searchParams.get('challenge');
        if (challenge) {
            setFormData((prev) => ({
                ...prev,
                projectDetails: challenge,
            }));
        }
    }, [searchParams]);
    // Auto-save progress when form data changes (debounced)
    useEffect(() => {
        // Clear existing timeout
        if (saveTimeoutRef.current) {
            clearTimeout(saveTimeoutRef.current);
        }
        // Only auto-save if we have at least a name or email
        if (formData.name || formData.email) {
            // Debounce: save 1 second after user stops typing
            saveTimeoutRef.current = setTimeout(() => {
                saveProgress();
            }, 1000);
        }
        // Cleanup timeout on unmount
        return () => {
            if (saveTimeoutRef.current) {
                clearTimeout(saveTimeoutRef.current);
            }
        };
    }, [formData, sessionId, searchParams]);
    // Save progress to backend (non-blocking, fire-and-forget)
    const saveProgress = () => {
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
        // Use fetch with keepalive for reliability, but don't await
        fetch(`${apiUrl}/intake/progress`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                sessionId,
                name: formData.name || undefined,
                email: formData.email || undefined,
                phone: formData.phone || undefined,
                company: formData.company || undefined,
                projectDetails: formData.projectDetails || undefined,
                budget: formData.budget || undefined,
                challenge: searchParams.get('challenge') || undefined,
            }),
            keepalive: true, // Keep request alive even if page navigates
        }).catch((error) => {
            // Silently log errors, don't block UI
            console.error('Error saving progress:', error);
        });
    };
    // Mark intake as abandoned when user navigates away
    useEffect(() => {
        const handleBeforeUnload = async () => {
            // Only mark as abandoned if email was entered and form is not completed
            if (formData.email && !submitSuccess) {
                try {
                    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
                    // Use sendBeacon for reliable delivery even on page unload
                    const blob = new Blob([JSON.stringify({ sessionId })], { type: 'application/json' });
                    navigator.sendBeacon(`${apiUrl}/intake/mark-abandoned`, blob);
                }
                catch (error) {
                    console.error('Error marking intake as abandoned:', error);
                }
            }
        };
        // Also handle visibility change (tab switch, minimize, etc.)
        const handleVisibilityChange = async () => {
            if (document.hidden && formData.email && !submitSuccess) {
                // User switched tabs or minimized - mark as potentially abandoned
                // Server will handle the 30-minute check
                try {
                    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
                    await fetch(`${apiUrl}/intake/mark-abandoned`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ sessionId }),
                        keepalive: true, // Keep request alive even if page unloads
                    });
                }
                catch (error) {
                    console.error('Error marking intake as abandoned:', error);
                }
            }
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [formData.email, submitSuccess, sessionId]);
    const totalSteps = 6;
    const questions = [
        {
            step: 1,
            title: "What's your name?",
            field: 'name',
            placeholder: 'Enter your full name',
            type: 'text',
        },
        {
            step: 2,
            title: "What's your email?",
            field: 'email',
            placeholder: 'your.email@example.com',
            type: 'email',
        },
        {
            step: 3,
            title: "What's your phone number?",
            field: 'phone',
            placeholder: '(888) 123-4567',
            type: 'tel',
        },
        {
            step: 4,
            title: "What's your company name?",
            field: 'company',
            placeholder: 'Enter your company name (optional)',
            type: 'text',
            optional: true,
        },
        {
            step: 5,
            title: "Tell us about your project",
            field: 'projectDetails',
            placeholder: 'Describe your project idea, goals, and what you hope to achieve...',
            type: 'textarea',
        },
        {
            step: 6,
            title: "What's your budget range?",
            field: 'budget',
            placeholder: 'Select a budget range',
            type: 'select',
            options: [
                'Under $10,000',
                '$10,000 - $25,000',
                '$25,000 - $50,000',
                '$50,000 - $100,000',
                '$100,000 - $250,000',
                'Over $250,000',
                'Not sure yet',
            ],
        },
    ];
    const currentQuestion = questions[currentStep - 1];
    const handleInputChange = async (value) => {
        const wasEmailEmpty = !formData.email;
        const isEmailField = currentQuestion.field === 'email';
        setFormData((prev) => ({
            ...prev,
            [currentQuestion.field]: value,
        }));
        // If email was just entered, save progress immediately to trigger started email
        if (isEmailField && wasEmailEmpty && value.trim()) {
            // Small delay to ensure state is updated, then save (non-blocking)
            setTimeout(() => {
                saveProgress();
            }, 100);
        }
    };
    const handlePhoneInputChange = (e) => {
        const value = e.target.value;
        // Remove all non-digits to get the raw number
        const digitsOnly = value.replace(/\D/g, '');
        // Limit to 10 digits
        const limitedDigits = digitsOnly.substring(0, 10);
        // Format the phone number
        let formatted = limitedDigits;
        if (limitedDigits.length >= 1) {
            formatted = '(' + limitedDigits.substring(0, 3);
        }
        if (limitedDigits.length >= 4) {
            formatted = '(' + limitedDigits.substring(0, 3) + ') ' + limitedDigits.substring(3);
        }
        if (limitedDigits.length >= 7) {
            formatted = '(' + limitedDigits.substring(0, 3) + ') ' + limitedDigits.substring(3, 6) + '-' + limitedDigits.substring(6);
        }
        setFormData((prev) => ({
            ...prev,
            phone: formatted,
        }));
    };
    const handleNext = () => {
        if (currentStep < totalSteps) {
            // Save progress in background (non-blocking)
            saveProgress();
            // Move to next step immediately without waiting
            setCurrentStep(currentStep + 1);
        }
    };
    const handleBack = () => {
        if (currentStep > 1) {
            setCurrentStep(currentStep - 1);
        }
    };
    const isStepValid = () => {
        const value = formData[currentQuestion.field];
        // Optional fields are always valid
        if (currentQuestion.optional) {
            return true;
        }
        if (currentQuestion.type === 'email') {
            return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
        }
        if (currentQuestion.type === 'tel') {
            return /^[\d\s\(\)\-]+$/.test(value) && value.replace(/\D/g, '').length >= 10;
        }
        return value.trim().length > 0;
    };
    const handleSubmit = async () => {
        if (!formData.recaptchaToken) {
            setSubmitError('Please complete the reCAPTCHA verification');
            return;
        }
        // Clear any pending auto-save
        if (saveTimeoutRef.current) {
            clearTimeout(saveTimeoutRef.current);
        }
        setIsSubmitting(true);
        setSubmitError(null);
        let hasShownSuccess = false;
        // Show success optimistically after a short delay (for better UX)
        const optimisticTimeout = setTimeout(() => {
            // If request is still pending after 500ms, show success optimistically
            // The actual request will complete in the background
            hasShownSuccess = true;
            setSubmitSuccess(true);
            setIsSubmitting(false);
            sessionStorage.removeItem('intake_session_id');
        }, 500);
        try {
            const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
            const response = await fetch(`${apiUrl}/intake/submit`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    sessionId,
                    name: formData.name,
                    email: formData.email,
                    phone: formData.phone,
                    company: formData.company,
                    projectDetails: formData.projectDetails,
                    budget: formData.budget,
                    challenge: searchParams.get('challenge') || undefined,
                    recaptchaToken: formData.recaptchaToken,
                }),
            });
            clearTimeout(optimisticTimeout);
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to submit form');
            }
            // Clear session ID on success
            if (!hasShownSuccess) {
                sessionStorage.removeItem('intake_session_id');
                setSubmitSuccess(true);
                setIsSubmitting(false);
            }
        }
        catch (error) {
            clearTimeout(optimisticTimeout);
            // Only show error if we haven't already shown success optimistically
            if (!hasShownSuccess) {
                setSubmitError(error instanceof Error ? error.message : 'An error occurred. Please try again.');
                setIsSubmitting(false);
            }
            else {
                // If we already showed success, just log the error silently
                console.error('Error submitting form (already shown success):', error);
            }
        }
    };
    const handleRecaptchaChange = (token) => {
        setFormData((prev) => ({
            ...prev,
            recaptchaToken: token,
        }));
    };
    if (submitSuccess) {
        return (_jsxs(_Fragment, { children: [_jsx(SEO, { title: "Thank You - Project Intake Form | BuzBiz", description: "Thank you for submitting your project details. We'll get back to you soon." }), _jsxs("div", { className: "intake-page", children: [_jsx(Header, {}), _jsx("main", { className: "intake-container", style: {
                                backgroundImage: 'url(/images/intake/network.jpg)',
                            }, children: _jsxs("div", { className: "success-message", children: [_jsx("div", { className: "success-icon", children: "\u2713" }), _jsx("h1", { children: "We got your message!" }), _jsx("p", { children: "We'll review your project details and get back to you as soon as possible." }), _jsx("button", { onClick: () => navigate('/'), className: "btn btn-primary", children: "Return to Home" })] }) }), _jsx(Footer, {})] })] }));
    }
    return (_jsxs(_Fragment, { children: [_jsx(SEO, { title: "Project Intake Form | BuzBiz", description: "Tell us about your project and we'll help bring your vision to life." }), _jsxs("div", { className: "intake-page", children: [_jsx(Header, {}), _jsx("main", { className: "intake-container", style: {
                            backgroundImage: 'url(/images/intake/network.jpg)',
                        }, children: _jsxs("div", { className: "intake-wizard", children: [_jsxs("div", { className: "wizard-profile", children: [_jsx("div", { className: "profile-image-container", children: _jsx("img", { src: "/images/about/mitchell-lewis-u.png", alt: "Mitchell Lewis - Founder & CEO of BuzBiz", className: "profile-image", onError: (e) => {
                                                    e.currentTarget.style.display = 'none';
                                                } }) }), _jsxs("div", { className: "profile-info", children: [_jsx("h2", { children: "Hi, I'm Mitchell" }), _jsx("p", { children: "Let's get started on your project" })] })] }), _jsx("div", { className: "progress-bar", children: _jsx("div", { className: "progress-fill", style: { width: `${(currentStep / totalSteps) * 100}%` } }) }), _jsxs("div", { className: "question-section", children: [_jsxs("div", { className: "question-number", children: ["Question ", currentStep, " of ", totalSteps] }), _jsx("h1", { className: "question-title", children: currentQuestion.title }), currentQuestion.type === 'textarea' ? (_jsx("textarea", { className: "question-input", placeholder: currentQuestion.placeholder, value: formData[currentQuestion.field], onChange: (e) => handleInputChange(e.target.value), rows: 6, autoFocus: true })) : currentQuestion.type === 'select' ? (_jsx("div", { className: "select-options", children: currentQuestion.options?.map((option, index) => (_jsx("button", { type: "button", className: `select-option ${formData[currentQuestion.field] === option ? 'selected' : ''}`, onClick: () => handleInputChange(option), children: option }, index))) })) : (_jsx("input", { type: currentQuestion.type, className: "question-input", placeholder: currentQuestion.placeholder, value: formData[currentQuestion.field], onChange: currentQuestion.type === 'tel'
                                                ? handlePhoneInputChange
                                                : (e) => handleInputChange(e.target.value), autoFocus: true })), submitError && _jsx("div", { className: "error-message", children: submitError }), currentStep === totalSteps && (_jsx("div", { className: "recaptcha-container", children: _jsx(ReCAPTCHA, { sitekey: process.env.REACT_APP_RECAPTCHA_SITE_KEY || '', onChange: handleRecaptchaChange }) })), _jsxs("div", { className: "wizard-actions", children: [currentStep > 1 && (_jsx("button", { type: "button", onClick: handleBack, className: "btn btn-secondary", children: "\u2190 Back" })), currentStep < totalSteps ? (_jsx("button", { type: "button", onClick: handleNext, className: "btn btn-primary", disabled: !isStepValid(), children: "Next \u2192" })) : (_jsx("button", { type: "button", onClick: handleSubmit, className: "btn btn-primary", disabled: !isStepValid() || isSubmitting || !formData.recaptchaToken, children: isSubmitting ? 'Submitting...' : 'Submit' }))] })] })] }) }), _jsx(Footer, {})] })] }));
};
export default IntakeForm;
